import { useMemo, useState } from "react"
import { computed } from "mobx"
import { observer } from "mobx-react"
import { Button, Checkbox, Flex, Form, Input, Select } from "antd"
import { useForm, useWatch } from "antd/es/form/Form"

// @ts-ignore
import termsPdf from "@src/app/assets/pdf/cryptix_terms.pdf"
// @ts-ignore
import privacyPolicyTransferPdf from "@src/app/assets/pdf/PP_Transferio.pdf"
// @ts-ignore
import privacyPolicyPdf from "@src/app/assets/pdf/privacy_policy.pdf"
// @ts-ignore
import termsTransferPdf from "@src/app/assets/pdf/T&C_Transferio.pdf"
// @ts-ignore
import referralTerms from "@src/app/assets/pdf/terms_and_conditions_cryptix_referral.pdf"
import { ModalInfo } from "@src/app/components/ModalInfo"
import uiStore from "@src/app/uiStore"
import { USER_TYPES_OPTIONS } from "@src/pages/registration/constants"
import { RegistrationAuthRequest } from "@src/pages/registration/store"
import { RegistrationPasswordField } from "@src/pages/registration/ui/authForm/ui/RegistrationPasswordField"
import {
  MerchantResponseUserTypeEnum,
  SignupDetailsRequestUserTypeEnum,
} from "@src/shared/api/generated"
import { SubmitButton } from "@src/shared/componemts/SubmitButton"
import { useCatchFormErrors } from "@src/shared/form/base_error"
import { useBreakpoint } from "@src/shared/hooks/useBreakpoints"

type Props = {
  forceUserType?: SignupDetailsRequestUserTypeEnum
  isLoading: boolean
  onSubmit: (args: RegistrationAuthRequest) => Promise<void>
  refId: null | string
}
export const RegistrationFormAuth = observer(
  ({ forceUserType, isLoading, onSubmit, refId }: Props) => {
    const email = new URLSearchParams(window.location.search).get("email")
    const [isModalOpen, setIsModalOpen] = useState(false)
    const { isMobile: isMobBreakpoint } = useBreakpoint()
    const { isTransferioTheme } = uiStore()
    const [content, setContent] = useState("")
    const [form] = useForm()
    const { submit: onSubmitForm } = useCatchFormErrors(form, (values) => {
      return submit(values)
    })

    const pdfMap: Record<string, string> = computed(() => {
      return {
        merchant: isTransferioTheme ? termsTransferPdf : termsPdf,
        policy: isTransferioTheme ? privacyPolicyTransferPdf : privacyPolicyPdf,
        referrer: referralTerms,
      }
    }).get()

    const { smallerThanLg } = useBreakpoint()

    const forceType = useMemo(() => {
      return USER_TYPES_OPTIONS.find((t) => t.value === forceUserType)
    }, [forceUserType])

    const merchantType = useMemo(() => {
      return USER_TYPES_OPTIONS.find(
        (t) => t.value === SignupDetailsRequestUserTypeEnum.Merchant,
      )
    }, [forceUserType])

    const userType = useWatch("user_type", form)

    const onModalOpen = (key: string) => {
      const isMobile = /Android|iPhone|iPad|iPod|Windows Phone/i.test(
        navigator.userAgent,
      )

      if (!(isMobile || isMobBreakpoint)) {
        setContent(key)
        return setIsModalOpen(true)
      }

      try {
        const rawUrl = pdfMap[key]

        if (typeof rawUrl !== "string" || !rawUrl.trim()) {
          console.warn(
            `[PDF] Пустой или некорректный URL для ключа: "${key}"`,
            rawUrl,
          )
          return
        }

        // Проверка валидности URL
        try {
          new URL(rawUrl, window.location.origin)
        } catch (urlErr) {
          console.error(`[PDF] Невалидный URL: "${rawUrl}"`, urlErr)
          return
        }

        setTimeout(() => {
          try {
            const newWindow = window.open(rawUrl, "_blank")

            if (!newWindow) {
              console.warn("[PDF] window.open заблокирован. Пробуем скачать.")
            }
          } catch (downloadErr) {
            console.error(
              "[PDF] Ошибка во время открытия или скачивания:",
              downloadErr,
            )
          }
        }, 0)
      } catch (err) {
        console.error("[PDF] Непредвиденная ошибка в useEffect:", err)
      }
    }

    const { submit } = useCatchFormErrors(form, onSubmit)

    return (
      <Form
        form={form}
        id="GENERATED_ID_REGISTRATIONFORMAUTH_FORM_VOID_1"
        layout="vertical"
        onFinish={onSubmitForm}
        style={{
          padding: smallerThanLg ? "32px 16px" : 40,
          width: "100%",
        }}
      >
        <Flex gap={16} vertical>
          {!isTransferioTheme && (
            <div>
              {forceUserType || refId ? (
                <Form.Item label={""}>
                  <Input
                    disabled
                    value={forceType?.label ?? merchantType?.label}
                  />
                </Form.Item>
              ) : (
                <Form.Item
                  id="GENERATED_ID_REGISTRATIONFORMAUTH_TSX_FORM.ITEM_VOID_1"
                  initialValue={SignupDetailsRequestUserTypeEnum.Merchant}
                  label="User Type"
                  name="user_type"
                >
                  <Select
                    disabled={Boolean(refId)}
                    options={USER_TYPES_OPTIONS}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              )}
            </div>
          )}

          <Form.Item
            id="GENERATED_ID_REGISTRATIONFORMAUTH_TSX_FORM.ITEM_VOID_2"
            initialValue={email || ""}
            label="Email"
            name="email"
            rules={[
              {
                message: "Must be a valid email address",
                type: "email",
              },
              {
                message: "Please input your Email",
                required: true,
              },
            ]}
          >
            <Input placeholder="Enter your email" />
          </Form.Item>

          <RegistrationPasswordField />

          <Flex gap={32} vertical>
            <Form.Item
              id="GENERATED_ID_REGISTRATIONFORMAUTH_TSX_FORM.ITEM_VOID_4"
              name="tos"
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error("You should accept terms of service"),
                        ),
                },
              ]}
              valuePropName="checked"
            >
              <Checkbox id="GENERATED_ID_REGISTRATIONFORMAUTH_CHECKBOX_I_AGREE_WITH_1">
                I agree with{" "}
                <Button
                  id="GENERATED_ID_REGISTRATIONFORMAUTH_BUTTON_PRIVACY_POLICY_1"
                  onClick={() => onModalOpen("policy")}
                  style={{ padding: 0 }}
                  type="link"
                >
                  Privacy Policy
                </Button>{" "}
                &{" "}
                <Button
                  id="GENERATED_ID_REGISTRATIONFORMAUTH_BUTTON_TERMS_OF_SERVICE_1"
                  onClick={() =>
                    onModalOpen(
                      forceType?.value ??
                        userType ??
                        MerchantResponseUserTypeEnum.Merchant,
                    )
                  }
                  style={{ height: "fit-content", padding: 0 }}
                  type="link"
                >
                  Terms of Service
                </Button>
              </Checkbox>
            </Form.Item>

            <SubmitButton
              buttonProps={{
                loading: isLoading,
                size: "middle",
                style: { margin: "0 auto", width: "fit-content" },
                type: "primary",
              }}
              form={form}
            >
              Next step
            </SubmitButton>
          </Flex>
        </Flex>

        <ModalInfo
          contentKey={content}
          onClose={() => setIsModalOpen(false)}
          open={isModalOpen}
        />
      </Form>
    )
  },
)
